'use client';

import { usePathname } from 'next/navigation';
import { ROUTES } from '@/constants/application-urls';
import HeaderDictionary from '@/types/dictionaries/Header';
import FavoriteBorderOutlined from '@mui/icons-material/FavoriteBorderOutlined';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined';
import Login from '@mui/icons-material/Login';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import { signInSignOut } from '../Header/components/actions';
import LocalizedLink from '../LocalizedLink';

interface AccountMenuListProps {
	dict: HeaderDictionary;
	menuType?: 'header' | 'myAccount';
	showInvoices: boolean;
	handleClose?: () => void;
}

const AccountMenuList = ({ dict, handleClose, showInvoices, menuType = 'header' }: AccountMenuListProps) => {
	const pathname = usePathname();
	const isHeaderMenu = menuType === 'header';

	const menuItems = [
		{
			id: 'orders',
			icon: <Inventory2Outlined sx={{ fontSize: '22px', mr: 2, mt: '1px' }} />,
			url: ROUTES.ORDERS,
			title: dict.orders,
			onClick: handleClose,
			testId: 'header-orders-menu-item',
		},
		...(showInvoices
			? [
					{
						id: 'invoices',
						icon: <Inventory2Outlined sx={{ fontSize: '22px', mr: 2, mt: '1px' }} />,
						url: ROUTES.INVOICES,
						title: dict.invoices,
						onClick: handleClose,
						testId: 'header-invoices-menu-item',
					},
				]
			: []),
		{
			id: 'addresses',
			icon: <LocationOnOutlined sx={{ fontSize: '22px', mr: 2, mt: '1px' }} />,
			url: ROUTES.ADDRESSES,
			title: dict.addresses,
			onClick: handleClose,
			testId: 'header-addresses-menu-item',
		},
		{
			id: 'favorites',
			icon: <FavoriteBorderOutlined sx={{ fontSize: '22px', mr: 2, mt: '1px' }} />,
			url: ROUTES.FAVORITES,
			title: dict.favorites,
			onClick: handleClose,
			testId: 'header-favorites-menu-item',
		},
		...(isHeaderMenu
			? [
					{
						id: 'logout',
						icon: <Login sx={{ fontSize: '22px', mr: 2 }} />,
						title: dict.logout,
						onClick: async () => {
							await signInSignOut();
							handleClose?.();
						},
						testId: 'header-account-logout-button',
					},
				]
			: []),
	];

	return (
		<>
			{menuItems.map(({ id, icon, title, url, onClick, testId }) => {
				const isSelected = url && pathname.endsWith(url);

				const content = (
					<>
						{icon}
						{title}
					</>
				);

				const menuItemStyles = {
					fontSize: isHeaderMenu ? '18px' : '16px',
					cursor: 'auto',
					p: 0,
				};

				const myAccountMenuItemStyles = {
					minWidth: '208px',
					borderBottom: '1px solid',
					borderColor: 'grey.light',
					marginRight: '32px',
				};

				return (
					<MenuItem key={id} sx={isHeaderMenu ? menuItemStyles : { ...menuItemStyles, ...myAccountMenuItemStyles }}>
						{url ? (
							<LocalizedLink
								href={url}
								onClick={onClick}
								sx={{
									color: isSelected ? 'primary.main' : 'black.main',
									textDecoration: 'none',
									width: '100%',
									display: 'flex',
									p: isHeaderMenu ? '15px 20px' : '16px',
									'& .MuiSvgIcon-root': {
										color: isSelected ? 'primary.main' : 'inherit',
									},
								}}
								data-testid={testId}
							>
								{content}
							</LocalizedLink>
						) : (
							<Button
								onClick={onClick}
								sx={{
									p: '15px 20px',
									'&:hover': {
										bgcolor: 'transparent',
									},
									fontSize: '18px',
									color: 'black.main',
									width: '100%',
									justifyContent: 'start',
									borderRadius: 0,
								}}
								data-testid={testId}
							>
								{content}
							</Button>
						)}
					</MenuItem>
				);
			})}
		</>
	);
};

export default AccountMenuList;
